import { allows } from "../page/viewerActions";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { AgentSetupRecord } from "../../../api/generated/threadLive/serverToBrowser";
import { newClientKey, requestTurn } from "../../../api/threads/threadsApi";
import ErrorNotice from "../../../errors/ErrorNotice";
import { useSubmission } from "../../../shared/state/useSubmission";
import AppButton from "../../../ui/buttons/AppButton";
import DefinitionListItem from "../../../ui/layout/DefinitionListItem";
import { useThreadContext } from "../page/threadPageContext";
import type { TurnStep } from "./logBlocks";

/**
 * What an agent turn ran with (agent-profiles.md AP-3): the setup, and for the user's own
 * setup the names, counts and digests of its personal layer. Names are shown as provenance
 * (AP-Q2, proposed); never file contents.
 */
export function AgentSetupSummary({ setup }: { setup: AgentSetupRecord }) {
  const { t } = useTranslation();
  const personal = setup.personal_layer;
  return (
    <>
      <DefinitionListItem term={t("threads.turn.agentSetup.title")}>
        {t(`threads.turn.agentSetup.${setup.setup}`)}
        {personal === null
          ? null
          : ` · ${t("threads.turn.agentSetup.counts", {
              skills: t("threads.turn.agentSetup.skills", { count: personal.skills.length }),
              servers: t("threads.turn.agentSetup.servers", { count: personal.mcp_servers.filter((server) => server.loaded).length }),
              files: t("threads.turn.agentSetup.files", { count: personal.instruction_files.length }),
            })}`}
      </DefinitionListItem>
      {personal === null ? null : (
        <>
          {personal.skills.length === 0 ? null : (
            <DefinitionListItem term={t("threads.turn.agentSetup.personalSkills")}>{personal.skills.map((skill) => skill.name).join(", ")}</DefinitionListItem>
          )}
          {personal.mcp_servers.length === 0 ? null : (
            <DefinitionListItem term={t("threads.turn.agentSetup.servers")}>
              {personal.mcp_servers
                .map((server) => t(server.loaded ? "threads.turn.agentSetup.serverLoaded" : "threads.turn.agentSetup.serverOff", { name: server.name }))
                .join(", ")}
            </DefinitionListItem>
          )}
          {personal.plugins.length === 0 ? null : (
            <DefinitionListItem term={t("threads.turn.agentSetup.plugins")}>
              {personal.plugins
                .map((plugin) => t(plugin.loaded ? "threads.turn.agentSetup.serverLoaded" : "threads.turn.agentSetup.serverOff", { name: plugin.name }))
                .join(", ")}
            </DefinitionListItem>
          )}
          {Object.keys(personal.hooks).length === 0 ? null : (
            <DefinitionListItem term={t("threads.turn.agentSetup.hooks")}>
              {Object.entries(personal.hooks).map(([event, count]) => `${event} ${String(count)}`).join(", ")}
            </DefinitionListItem>
          )}
          <DefinitionListItem term={t("threads.turn.agentSetup.memory")}>
            {t("threads.turn.agentSetup.memoryRead", { read: t(`threads.turn.agentSetup.memoryStates.${personal.memory_read}`) })}
          </DefinitionListItem>
        </>
      )}
      <DefinitionListItem term={t("threads.turn.agentSetup.digest")} valueClassName="font-mono break-all">{setup.setup_digest}</DefinitionListItem>
    </>
  );
}

/**
 * "Try with DMW only" (AP-6): a failed turn that ran with the user's own setup is requested
 * again without it, to tell a personal hook, plugin or instruction file from a DMW problem.
 */
export function TryWithDmwOnly({ turnStep }: { turnStep: TurnStep | null }) {
  const { t } = useTranslation();
  const { threadId, overview } = useThreadContext();
  const { state, submit } = useSubmission();
  // One key per click: a retry after a network failure is the same request.
  const [clientRequestId, setClientRequestId] = useState(newClientKey);
  const stepKey = turnStep?.stepKey ?? null;
  // Module C's decision for this viewer: the reply's request, or the step's run (W-22 slice 2).
  if (!(stepKey === null ? allows(overview.viewer, "request_reply") : allows(overview.viewer, "run_step", stepKey))) return null;
  return (
    <div className="space-y-1">
      <AppButton
        type="button"
        variant="secondary"
        size="sm"
        disabled={state.status === "submitting"}
        onClick={() => {
          submit(async () => {
            await requestTurn(threadId, { kind: stepKey === null ? "reply" : "step", stepKey, clientRequestId, agentSetup: "dmw_only" });
            setClientRequestId(newClientKey());
          });
        }}
      >
        {t("threads.turn.agentSetup.tryDmwOnly")}
      </AppButton>
      <p className="app-text-muted text-xs">{t("threads.turn.agentSetup.tryDmwOnlyHelp")}</p>
      {state.status === "failed" ? <ErrorNotice error={state.error} /> : null}
    </div>
  );
}
